import { useCallback, useState } from 'react';
import { EFFECTS } from '../data/effects';
import { useSectionNavigation } from '../hooks/useSectionNavigation';

interface FunhouseProps {
  setActiveSection: (section: string) => void;
  onTriggerEffect: (effectId: string, origin?: { x: number; y: number }) => void;
  lensEnabled: boolean;
  snitchActive: boolean;
}

// Fallback if `scrollend` never fires for some reason — see
// runAfterProjectsScroll below.
const SCROLL_SETTLE_TIMEOUT_MS = 900;
// Browsers without `scrollend` (e.g. Safari) poll the scroll position on
// rAF instead of relying on that flat timeout — see runAfterProjectsScroll.
// Considered "settled" once the position has stopped changing for this long...
const SCROLL_POLL_IDLE_MS = 150;
// ...with this as an absolute backstop regardless, same intent as
// SCROLL_SETTLE_TIMEOUT_MS above but generous enough to not cut a
// genuinely long scroll short.
const SCROLL_POLL_HARD_CAP_MS = 2500;

function getScrollPosition(container: HTMLElement | Window): number {
  return container instanceof Window ? window.scrollY : container.scrollTop;
}

// Desktop scrolls its <main>, mobile scrolls the window — same dual-layout
// split useSectionNavigation's own scrollToSection already handles. Finds
// the container via the currently-*visible* #projects section (there are
// two, one per layout, only one ever rendered with a size) rather than a
// bare `document.querySelector('main')`, which would always resolve to the
// desktop layout's <main> regardless of viewport since it comes first in
// DOM order.
//
// #projects itself can be display:none right now even on desktop — About.tsx
// hides an inactive section's own <section> box (e.g. while Funhouse is the
// active section) so it doesn't reserve empty margin — so this falls back to
// whichever <main> is actually visible instead of giving up and returning
// `window`, which would be wrong on desktop.
function getProjectsScrollContainer(): HTMLElement | Window {
  if (window.innerWidth < 1024) return window;

  const sections = document.querySelectorAll<HTMLElement>('#projects');
  const visibleSection = Array.from(sections).find((el) => el.getClientRects().length > 0);
  const fromSection = visibleSection?.closest('main') as HTMLElement | null;
  if (fromSection) return fromSection;

  const mains = document.querySelectorAll<HTMLElement>('main');
  const visibleMain = Array.from(mains).find((el) => el.getClientRects().length > 0);
  return visibleMain ?? window;
}

const Funhouse: React.FC<FunhouseProps> = ({ setActiveSection, onTriggerEffect, lensEnabled, snitchActive }) => {
  const navigateToSection = useSectionNavigation(setActiveSection);
  // A one-time check, same as every other prefers-reduced-motion read in
  // this codebase (App.tsx, GoldenSnitchEffect, GravitationalLens) — not
  // wired to a live 'change' listener, since nothing else here is either.
  const [reducedMotion] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );

  // Every effect except the Gravity Lens toggle (handled separately below)
  // navigates to Projects first, waits for that scroll to actually settle,
  // then triggers — see improvements/14-funhouse-quirks-section.md.
  const runAfterProjectsScroll = useCallback(
    (effectId: string, origin?: { x: number; y: number }) => {
      const container = getProjectsScrollContainer();
      const alreadyAtTop = getScrollPosition(container) <= 1;

      let settled = false;
      // Mutable cells (not bare `let`s) so `finish` — defined before the
      // timer/rAF handle it needs to cancel even exist — can still
      // reference them without a temporal-dead-zone error on the
      // "already at top" path, which calls finish() immediately and never
      // sets either.
      const timerBox: { id: number | undefined } = { id: undefined };
      const rafBox: { id: number | undefined } = { id: undefined };
      const finish = () => {
        if (settled) return;
        settled = true;
        container.removeEventListener('scrollend', finish);
        if (timerBox.id !== undefined) window.clearTimeout(timerBox.id);
        if (rafBox.id !== undefined) window.cancelAnimationFrame(rafBox.id);
        onTriggerEffect(effectId, origin);
      };

      navigateToSection('projects');

      if (alreadyAtTop) {
        finish();
        return;
      }

      // Read into a plain boolean first, not inlined into the `if` below —
      // TypeScript's `in`-narrowing treats `window`'s static type as always
      // having `onscrollend` (it's a real, if possibly-null, DOM property),
      // so guarding directly on `'onscrollend' in window` narrows `window`
      // itself to `never` in the branch below and breaks every window.*
      // call there. A separate boolean sidesteps that entirely.
      const supportsScrollEnd = 'onscrollend' in window;

      if (supportsScrollEnd) {
        container.addEventListener('scrollend', finish, { once: true });
        timerBox.id = window.setTimeout(finish, SCROLL_SETTLE_TIMEOUT_MS);
        return;
      }

      // No native scrollend support (Safari, as of this writing) — poll the
      // scroll position instead of just eating the flat timeout every time,
      // so a short scroll doesn't have to wait out the full fallback window
      // and a slow/long one still gets to finish naturally rather than
      // being cut off at a fixed point. "Settled" once the position hasn't
      // moved for SCROLL_POLL_IDLE_MS, with SCROLL_POLL_HARD_CAP_MS as an
      // absolute backstop regardless.
      const pollStart = performance.now();
      let lastPos = getScrollPosition(container);
      let lastChangeAt = pollStart;

      const poll = (now: number) => {
        const pos = getScrollPosition(container);
        if (pos !== lastPos) {
          lastPos = pos;
          lastChangeAt = now;
        }
        if (now - lastChangeAt >= SCROLL_POLL_IDLE_MS || now - pollStart >= SCROLL_POLL_HARD_CAP_MS) {
          finish();
          return;
        }
        rafBox.id = window.requestAnimationFrame(poll);
      };
      rafBox.id = window.requestAnimationFrame(poll);
    },
    [navigateToSection, onTriggerEffect]
  );

  const handleTileClick = (effectId: string, e: React.MouseEvent<HTMLButtonElement>) => {
    if (effectId === 'gravity-lens') {
      // A toggle, not a one-shot trigger — flips in place, no navigation.
      onTriggerEffect('gravity-lens');
      return;
    }
    if (effectId === 'golden-snitch' && snitchActive) {
      // Already loose somewhere on screen — recall it in place. App.tsx
      // ignores origin on this branch, and scrolling away first would just
      // be a pointless detour before stopping it.
      onTriggerEffect('golden-snitch');
      return;
    }
    const origin =
      effectId === 'golden-snitch'
        ? (() => {
            const r = e.currentTarget.getBoundingClientRect();
            return { x: r.left + r.width / 2, y: r.top + r.height / 2 };
          })()
        : undefined;
    runAfterProjectsScroll(effectId, origin);
  };

  return (
    <section id="funhouse" className="scroll-mt-20 mb-12 lg:mb-16">
      <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold font-mono mb-6 lg:mb-8 text-dev-text">
        <span className="text-dev-accent">//</span> Funhouse
      </h2>

      <p className="text-dev-text-muted text-sm md:text-base mb-6 lg:mb-8">
        Every playful effect built into this site, listed openly — click a tile to run it.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 md:gap-5 lg:gap-6">
        {EFFECTS.map((effect) => {
          const isDisabled = Boolean(effect.disabledOnReducedMotion) && reducedMotion;
          const isLens = effect.id === 'gravity-lens';
          const label = isLens ? `${effect.label}: ${lensEnabled ? 'On' : 'Off'}` : effect.label;

          return (
            <button
              key={effect.id}
              type="button"
              disabled={isDisabled}
              aria-pressed={isLens ? lensEnabled : undefined}
              onClick={(e) => handleTileClick(effect.id, e)}
              className={`dev-card group flex flex-col items-start gap-1.5 p-4 md:p-5 text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-dev-accent ${
                isDisabled ? 'opacity-50 cursor-not-allowed hover:border-dev-border' : ''
              }`}
            >
              <span
                className={`font-mono font-semibold text-base ${
                  isDisabled ? 'text-dev-text-dimmed' : 'text-dev-text group-hover:text-dev-accent transition-colors'
                }`}
              >
                {label}
              </span>
              <span className="text-dev-text-muted text-sm leading-relaxed">{effect.blurb}</span>
              {isDisabled && (
                <span className="text-dev-text-dimmed text-xs font-mono mt-0.5">
                  Disabled — reduced motion is on
                </span>
              )}
            </button>
          );
        })}
      </div>
    </section>
  );
};

export default Funhouse;
